import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import './App.css';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SubscriptionEnforcementGate from './components/SubscriptionEnforcementGate';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AthletesPage from './pages/AthletesPage';
import AthleteDetailPage from './pages/AthleteDetailPage';
import VenuesPage from './pages/VenuesPage';
import VenueDetailPage from './pages/VenueDetailPage';
import LessonsPage from './pages/LessonsPage';
import LessonDetailPage from './pages/LessonDetailPage';
import ReportsPage from './pages/ReportsPage';
import ProfilePage from './pages/ProfilePage';
import VenueManagerView from './pages/VenueManagerView';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AthleteRegisterPage from './pages/athlete/AthleteRegisterPage';
import AthleteDashboardPage from './pages/athlete/AthleteDashboardPage';
import AthleteProfilePage from './pages/athlete/AthleteProfilePage';

const ProtectedRoute = ({ children, requireCoach = false, requireAthlete = false }) => {
  const { user, userType, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (requireCoach && userType !== 'coach') {
    return <Navigate to="/athlete/dashboard" />;
  }

  if (requireAthlete && userType !== 'athlete') {
    return <Navigate to="/lessons" />;
  }

  if (requireCoach) {
    return <SubscriptionEnforcementGate>{children}</SubscriptionEnforcementGate>;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, userType, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={userType === 'athlete' ? '/athlete/dashboard' : '/lessons'} />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <div className="noise-overlay" />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requireCoach>
              <Navigate to="/lessons" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/athletes"
          element={
            <ProtectedRoute requireCoach>
              <AthletesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/athletes/:id"
          element={
            <ProtectedRoute requireCoach>
              <AthleteDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venues"
          element={
            <ProtectedRoute requireCoach>
              <VenuesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venues/:id"
          element={
            <ProtectedRoute requireCoach>
              <VenueDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lessons"
          element={
            <ProtectedRoute requireCoach>
              <LessonsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lessons/:id"
          element={
            <ProtectedRoute requireCoach>
              <LessonDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute requireCoach>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute requireCoach>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/athlete/register"
          element={
            <PublicRoute>
              <AthleteRegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/athlete/dashboard"
          element={
            <ProtectedRoute requireAthlete>
              <AthleteDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/athlete/profile"
          element={
            <ProtectedRoute requireAthlete>
              <AthleteProfilePage />
            </ProtectedRoute>
          }
        />
        <Route path="/venue-manager/:token" element={<VenueManagerView />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
      <Toaster position="top-right" theme="dark" richColors />
    </BrowserRouter>
  );
};

function App() {
  useEffect(() => {
    const isAndroid = /Android/i.test(navigator.userAgent || '');
    const isPhone = window.matchMedia('(max-width: 767px)').matches;
    if (isAndroid && isPhone) {
      document.body.classList.add('android-device');
    }
    const locale = navigator.language || navigator.userLanguage || 'en';
    document.documentElement.setAttribute('lang', locale);
    // Safety: clear any leftover scroll-lock styles on first load
    document.body.classList.remove('dialog-open');
    document.documentElement.style.removeProperty('overflow');
    const appScroll = document.querySelector('.app-scroll');
    if (appScroll) {
      appScroll.style.removeProperty('position');
      appScroll.style.removeProperty('top');
      appScroll.style.removeProperty('left');
      appScroll.style.removeProperty('right');
      appScroll.style.removeProperty('width');
      appScroll.style.removeProperty('overflow');
    }
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('height');
    document.body.style.removeProperty('overflow');
    return () => {
      document.body.classList.remove('android-device');
    };
  }, []);

  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
